export type AgentHostingState = 'reserved'|'reservation_aborting'|'queued'|'provisioning'|'awaiting_authorization'|'ready'|'busy'|'idle'|
  'stopped'|'failed'|'ownership_ambiguous'|'expired'|'deleting'|'deleted'|'deletion_failed';

export interface PrivateRuntimeStatusView {
  state: 'awaiting_preparation'|'preparing'|'awaiting_authorization'|'starting'|'running'|'stopping'|'expired'|'purged'|'recovery_unresolved'|'unknown';
  freshness: 'fresh'|'stale'|'unknown';
  revision: number|null;
  startedAtMs: number|null;
  expiresAtMs: number|null;
  hostObservedAtMs: number|null;
  chainObservedAtMs: number|null;
  serverNowMs: number;
  statusValidUntilMs: number|null;
  supervisorGeneration: number|null;
  invocationId: string|null;
  cleanup: 'not_required'|'pending'|'verified'|'unknown';
}

export interface PrivateAgentStatus {
  schemaVersion: 2;
  protocol: 'registry-v2';
  chainId: 8453;
  registryAddress: `0x${string}`;
  agentId: string;
  accountAddress: `0x${string}`;
  observedAtMs: number;
  runtime: { state: AgentHostingState; createdAtMs: number|null; startedAtMs: number|null; expiresAtMs: number|null; deletedAtMs: number|null };
  runtimeStatus: PrivateRuntimeStatusView;
}

const STATUS_KEYS = 'accountAddress,agentId,chainId,observedAtMs,protocol,registryAddress,runtime,runtimeStatus,schemaVersion';
const LEGACY_KEYS = 'createdAtMs,deletedAtMs,expiresAtMs,startedAtMs,state';
const RUNTIME_STATUS_KEYS = 'chainObservedAtMs,cleanup,expiresAtMs,freshness,hostObservedAtMs,invocationId,revision,serverNowMs,startedAtMs,state,statusValidUntilMs,supervisorGeneration';
const legacyStates = new Set<AgentHostingState>([
  'reserved','reservation_aborting','queued','provisioning','awaiting_authorization','ready','busy','idle','stopped','failed',
  'ownership_ambiguous','expired','deleting','deleted','deletion_failed',
]);
const runtimeStates = new Set([
  'awaiting_preparation','preparing','awaiting_authorization','starting','running','stopping','expired','purged','recovery_unresolved','unknown',
]);
const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);
const exactKeys = (value: Record<string, unknown>, expected: string) => Object.keys(value).sort().join(',') === expected;
const safeTime = (value: unknown): value is number => Number.isSafeInteger(value) && (value as number) >= 0;
const nullableTime = (value: unknown): value is number|null => value === null || safeTime(value);
const address = (value: unknown): value is `0x${string}` => typeof value === 'string' && /^0x[0-9a-f]{40}$/.test(value);
const uuid = (value: unknown): value is string => typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(value);

function validRuntimeStatus(value: unknown): value is PrivateRuntimeStatusView {
  if (!isRecord(value) || !exactKeys(value, RUNTIME_STATUS_KEYS) ||
      typeof value.state !== 'string' || !runtimeStates.has(value.state) ||
      typeof value.freshness !== 'string' || !['fresh','stale','unknown'].includes(value.freshness) ||
      typeof value.cleanup !== 'string' || !['not_required','pending','verified','unknown'].includes(value.cleanup) ||
      (value.revision !== null && (!Number.isSafeInteger(value.revision) || (value.revision as number) < 1)) ||
      !nullableTime(value.startedAtMs) || !nullableTime(value.expiresAtMs) || !nullableTime(value.hostObservedAtMs) ||
      !nullableTime(value.chainObservedAtMs) || !safeTime(value.serverNowMs) || !nullableTime(value.statusValidUntilMs) ||
      (value.supervisorGeneration !== null && (!Number.isSafeInteger(value.supervisorGeneration) || (value.supervisorGeneration as number) < 1)) ||
      (value.invocationId !== null && !uuid(value.invocationId))) return false;

  if (value.startedAtMs !== null && value.expiresAtMs !== null && value.expiresAtMs <= value.startedAtMs) return false;
  if ((value.supervisorGeneration === null) !== (value.invocationId === null)) return false;
  const hasFreshProof = value.revision !== null && value.startedAtMs !== null && value.expiresAtMs !== null &&
    value.hostObservedAtMs !== null && value.chainObservedAtMs !== null && value.statusValidUntilMs !== null &&
    value.supervisorGeneration !== null && value.invocationId !== null;
  if (value.freshness === 'fresh') {
    const validUntilMs = value.statusValidUntilMs;
    const hostObservedAtMs = value.hostObservedAtMs;
    const chainObservedAtMs = value.chainObservedAtMs;
    return value.state === 'running' && hasFreshProof && validUntilMs !== null && hostObservedAtMs !== null &&
      chainObservedAtMs !== null && validUntilMs > value.serverNowMs &&
      value.startedAtMs !== null && value.expiresAtMs !== null && value.startedAtMs <= value.serverNowMs &&
      value.serverNowMs < value.expiresAtMs && validUntilMs <= value.expiresAtMs &&
      validUntilMs <= hostObservedAtMs + 30_000 && validUntilMs <= chainObservedAtMs + 30_000 &&
      hostObservedAtMs <= value.serverNowMs && chainObservedAtMs <= value.serverNowMs;
  }
  return value.state !== 'running';
}

/** Parse the exact private status DTO shared by the authenticated API and browser. */
export function parsePrivateAgentStatus(value: unknown, expectedRegistry: string, expectedAgentId: string): PrivateAgentStatus {
  if (!isRecord(value) || !exactKeys(value, STATUS_KEYS) || value.schemaVersion !== 2 || value.protocol !== 'registry-v2' ||
      value.chainId !== 8453 || value.registryAddress !== expectedRegistry.toLowerCase() || value.agentId !== expectedAgentId ||
      !address(value.accountAddress) || !safeTime(value.observedAtMs) || !isRecord(value.runtime) ||
      !exactKeys(value.runtime, LEGACY_KEYS) || typeof value.runtime.state !== 'string' || !legacyStates.has(value.runtime.state as AgentHostingState) ||
      !nullableTime(value.runtime.createdAtMs) || !nullableTime(value.runtime.startedAtMs) ||
      !nullableTime(value.runtime.expiresAtMs) || !nullableTime(value.runtime.deletedAtMs) || !validRuntimeStatus(value.runtimeStatus)) {
    throw new Error('Invalid private status response');
  }
  const runtime = value.runtime as Record<string, unknown>;
  if ((runtime.startedAtMs !== null && runtime.createdAtMs !== null && (runtime.startedAtMs as number) < (runtime.createdAtMs as number)) ||
      (runtime.expiresAtMs !== null && runtime.startedAtMs !== null && (runtime.expiresAtMs as number) <= (runtime.startedAtMs as number)) ||
      (runtime.deletedAtMs !== null && runtime.startedAtMs !== null && (runtime.deletedAtMs as number) < (runtime.startedAtMs as number))) {
    throw new Error('Invalid private status response');
  }
  return value as unknown as PrivateAgentStatus;
}

/** Keep server freshness conservative using elapsed monotonic time since the request began. */
export function runtimeStatusIsDisplayFresh(
  view: PrivateRuntimeStatusView,
  requestStartedMonotonicMs: number,
  responseReceivedMonotonicMs: number,
  nowMonotonicMs: number,
): boolean {
  if (view.state !== 'running' || view.freshness !== 'fresh' || view.statusValidUntilMs === null ||
      !Number.isFinite(requestStartedMonotonicMs) || !Number.isFinite(responseReceivedMonotonicMs) || !Number.isFinite(nowMonotonicMs) ||
      requestStartedMonotonicMs < 0 || responseReceivedMonotonicMs < requestStartedMonotonicMs || nowMonotonicMs < responseReceivedMonotonicMs) return false;
  const validityWindowMs = view.statusValidUntilMs - view.serverNowMs;
  return validityWindowMs > 0 && nowMonotonicMs - requestStartedMonotonicMs < validityWindowMs;
}
